import { addDays, eachDayOfInterval, isSameDay, isSameMonth, isToday, startOfDay, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FieldLabel, MonoLabel, StatusChip } from '@/components/editorial';
import {
  CATEGORY_TOKENS,
  categoryLabel,
  compareEvents,
  eventTitle,
  eventWho,
  fmtHourGutter,
  fmtTimeShort,
  isCancelledEvent,
  type EventCategory,
  type UnifiedEvent,
} from '@/lib/calendar-display';
import { useCompanionName } from '@/hooks/useCompanionName';
import { useMediaQuery } from '@/features/chat-trf/useMediaQuery';
import { formatPreset, formatWeekday, weekStartFor } from '@/i18n/intl';

/**
 * Below Tailwind's `sm` (640 px) the week grid and the month chips do not fit
 * (#2419): the week scrolled sideways with two and a half days in view, and
 * month chips truncated to "9…". There the week reads as an agenda and a
 * month cell shows category dots.
 */
const PHONE_QUERY = '(max-width: 639px)';
/** Hour gutter + seven day columns that shrink to the card (#2497). */
const WEEK_GRID_COLUMNS = '56px repeat(7, minmax(0, 1fr))';

/** The day-of-month numeral ("22" · "22日"), through Intl like every other date part (#2109 R3-B5). */
const dayNumber = (d: Date) => formatPreset(d, { day: 'numeric' });
/** The short month name ("Sep" · "sept"). */
const monthShort = (d: Date) => formatPreset(d, { month: 'short' });

// ── Editorial event card (used by Agenda) ─────────────────────────────────

interface EventCardProps {
  event: UnifiedEvent;
  onClick?: (event: UnifiedEvent) => void;
}

function EventCard({ event, onClick }: EventCardProps) {
  const { t } = useTranslation(['calendar', 'common']);
  const companionName = useCompanionName();
  const who = eventWho(event, t);
  const tokens = CATEGORY_TOKENS[event.category];
  const isStevens = event.source === 'stevens-suggestion';
  const cancelled = isCancelledEvent(event);

  return (
    <Card
      asChild
      className={cn(
        'shadow-none',
        // #2433: a cancelled visit stays listed, faded and struck through.
        cancelled && 'opacity-60',
        // Kept on the Card (not the <button>) so tailwind-merge still drops
        // bg-card for the suggestion state — the Slot boundary does not merge.
        isStevens && 'border-dashed bg-status-ok/40 hover:bg-status-ok/60',
      )}
    >
    <button
      type="button"
      onClick={() => onClick?.(event)}
      className="relative flex items-start gap-4 w-full text-left transition-colors hover:border-stevens-indigo/30 px-5 py-3.5"
    >
      <span aria-hidden className={cn('absolute left-0 top-0 h-full w-[3px] rounded-l', tokens.rail)} />

      <div className="w-[80px] flex-shrink-0 font-mono text-caption leading-tight text-foreground">
        {event.allDay ? (
          // #2410: a date-only task has no clock and no end time.
          <div>{t('event.allDay')}</div>
        ) : (
          <>
            <div>{fmtTimeShort(event.start)}</div>
            <div className="text-stevens-ink-3 text-label">{t('event.until', { time: fmtTimeShort(event.end) })}</div>
          </>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <h4
          className={cn('font-serif',
            'text-card-title font-medium leading-snug text-foreground mb-0.5',
            isStevens && 'italic font-normal text-status-ok-fg',
            cancelled && 'line-through',
          )}
        >
          {eventTitle(event, t)}
        </h4>
        {who && (
          <div className="text-body-sm text-foreground/70">{who}</div>
        )}
        {event.note && (
          <div className="font-serif text-body-sm italic text-stevens-ink-3 mt-1">
            “{event.note}”
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <MonoLabel className="inline-flex items-center gap-1.5">
          <span className={cn('w-[7px] h-[7px] shrink-0 rounded-full', tokens.dot)} />
          {categoryLabel(event.category, companionName, t)}
        </MonoLabel>
        {cancelled && (
          <StatusChip tone="neutral">
            {t('apptStatus.cancelled')}
          </StatusChip>
        )}
        {event.urgent && (
          <StatusChip tone="danger">
            {t('event.needsHelper')}
          </StatusChip>
        )}
        {event.claimedByName && (
          <StatusChip tone="ok">
            {/* The name as the API gives it: splitting on a space picks the wrong part of a zh name. */}
            {t('event.claimedBy', { name: event.claimedByName })}
          </StatusChip>
        )}
        {event.source === 'stevens-suggestion' && (
          <MonoLabel className="text-status-ok-fg italic">
            {t('event.companionSuggestion', { companion: companionName })}
          </MonoLabel>
        )}
      </div>
    </button>
    </Card>
  );
}

// ── Agenda view ───────────────────────────────────────────────────────────

interface AgendaViewProps {
  events: UnifiedEvent[];
  rangeStart: Date;
  daysAhead?: number;
  onEventClick?: (event: UnifiedEvent) => void;
  todayNote?: string | null;
  /** When set, the empty "Nothing scheduled." line offers "Add an appointment" (#2532). */
  onAddAppointment?: () => void;
}

/** The empty-day line, with the add action when the page offers one. Shown once per view. */
function NothingScheduled({ className, onAddAppointment }: { className: string; onAddAppointment?: () => void }) {
  const { t } = useTranslation('calendar');
  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <span>{t('views.nothingScheduled')}</span>
      {onAddAppointment && (
        <Button type="button" size="sm" variant="outline" onClick={onAddAppointment} className="not-italic font-sans">
          {t('views.addAppointment')}
        </Button>
      )}
    </div>
  );
}

export function AgendaView({ events, rangeStart, daysAhead = 18, onEventClick, todayNote, onAddAppointment }: AgendaViewProps) {
  const { t } = useTranslation(['calendar', 'common']);
  const start = startOfDay(rangeStart);
  const days = eachDayOfInterval({ start, end: addDays(start, daysAhead) });

  return (
    <div className="flex flex-col gap-7">
      {days.map((day) => {
        // All-day first, then by time (#2410).
        const dayEvents = events.filter((e) => isSameDay(e.start, day)).sort(compareEvents);
        const isCurrentDay = isToday(day);
        if (!isCurrentDay && dayEvents.length === 0) return null;
        return (
          <div key={day.toISOString()} className="grid grid-cols-1 md:grid-cols-[88px_1fr] gap-4 md:gap-7 items-start">
            <div className="md:sticky md:top-20 flex flex-row md:flex-col items-baseline md:items-end gap-2 md:gap-1 md:text-right">
              <MonoLabel>
                {formatWeekday(day, { width: 'long' })}
              </MonoLabel>
              <span
                className={cn(
                  'leading-none',
                  isCurrentDay ? 'text-stevens-indigo' : 'text-foreground',
                )}
                style={{ fontSize: 36, fontWeight: 400, letterSpacing: '-0.02em' }}
              >
                {dayNumber(day)}
              </span>
              <MonoLabel>
                {monthShort(day)}
              </MonoLabel>
              {isCurrentDay && (
                <MonoLabel tone="indigo" className="self-end inline-flex rounded px-2 py-0.5 bg-stevens-indigo/10">
                  {t('views.today')}
                </MonoLabel>
              )}
              {isCurrentDay && todayNote && (
                <span
                  className="font-serif hidden md:block self-end max-w-[78px] text-caption italic text-status-ok-fg border-l-2 border-status-ok-fg/50 pl-2 mt-1.5 text-left leading-snug"
                >
                  “{todayNote}”
                </span>
              )}
            </div>

            <div className={cn('flex flex-col gap-2 pb-3 border-b border-border/60')}>
              {dayEvents.length > 0 ? (
                dayEvents.map((e) => <EventCard key={e.id} event={e} onClick={onEventClick} />)
              ) : (
                <NothingScheduled
                  className="font-serif text-body italic text-stevens-ink-3 py-2"
                  onAddAppointment={onAddAppointment}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Week view (vertical hour grid) ────────────────────────────────────────

interface WeekViewProps {
  events: UnifiedEvent[];
  weekAround: Date;
  onEventClick?: (event: UnifiedEvent) => void;
  /** Phone only: the empty week offers "Add an appointment" (#2532). */
  onAddAppointment?: () => void;
}

const HOUR_PX = 48;
const START_HOUR = 7;
const END_HOUR = 21;

export function WeekView({ events, weekAround, onEventClick, onAddAppointment }: WeekViewProps) {
  const { t, i18n } = useTranslation(['calendar', 'common']);
  const isPhone = useMediaQuery(PHONE_QUERY);
  // The week starts where the locale starts it; en stays Sunday (R3-B5).
  const start = startOfWeek(weekAround, { weekStartsOn: weekStartFor(i18n.language) });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const hours = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);

  if (isPhone) {
    // The same seven days as an agenda (#2419). The agenda hides an empty day
    // other than today, so a week with nothing in it says so.
    const weekHasRows = days.some((d) => isToday(d) || events.some((e) => isSameDay(e.start, d)));
    return weekHasRows ? (
      <AgendaView events={events} rangeStart={start} daysAhead={6} onEventClick={onEventClick} onAddAppointment={onAddAppointment} />
    ) : (
      // Plain body text: a serif italic aside is off the type ladder (design-grammar §2).
      <NothingScheduled className="text-body text-stevens-ink-3 py-2" onAddAppointment={onAddAppointment} />
    );
  }

  // #2410: all-day events sit in a strip above the hour grid, not at a clock
  // time inside it. No all-day event this week, no strip.
  const allDayEvents = events.filter((e) => e.allDay);
  const timedEvents = events.filter((e) => !e.allDay);
  const weekHasAllDay = days.some((d) => allDayEvents.some((e) => isSameDay(e.start, d)));

  return (
    // #2497: the day columns were `minmax(120px, 1fr)` — 896 px minimum — so
    // at 1280 px with the sidebar and companion column open the grid
    // scrolled sideways in a 626 px card and only Sun–Thu showed. Columns
    // now shrink to the card (`minmax(0, 1fr)`, ~81 px a day there); event
    // titles already truncate, so all seven days fit with no side scroll.
    <div data-testid="week-grid-card" className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: WEEK_GRID_COLUMNS }}>
        <div className="bg-muted border-b border-border" />
        {days.map((d) => {
          const cur = isToday(d);
          return (
            <div
              key={d.toISOString()}
              className={cn(
                'flex flex-col items-center gap-0.5 py-2.5 bg-muted border-b border-border font-mono',
                cur && 'bg-stevens-indigo/5',
              )}
            >
              <MonoLabel>{formatWeekday(d)}</MonoLabel>
              <span
 className={cn('text-[18px] tracking-tight', cur ? 'text-stevens-indigo font-medium': 'text-foreground')}
              >
                {dayNumber(d)}
              </span>
            </div>
          );
        })}

        {weekHasAllDay && (
          <div data-testid="week-all-day" className="contents">
            <div className="bg-muted border-r border-b border-border font-mono text-meta text-stevens-ink-3 text-end pe-1.5 pt-1">
              {t('event.allDay')}
            </div>
            {days.map((d) => (
              <div
                key={`allday-${d.toISOString()}`}
                className="flex flex-col gap-0.5 p-1 border-r border-b border-border last:border-r-0"
              >
                {allDayEvents.filter((e) => isSameDay(e.start, d)).map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => onEventClick?.(e)}
                    className={cn(
                      'truncate rounded px-2 py-0.5 text-start border text-label font-medium leading-tight',
                      CATEGORY_TOKENS[e.category].weekBg,
                      isCancelledEvent(e) && 'line-through opacity-60',
                    )}
                    title={eventTitle(e, t)}
                  >
                    {eventTitle(e, t)}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        <div
          className="bg-muted border-r border-border font-mono text-meta text-stevens-ink-3 text-right"
          style={{ gridRow: `span ${hours.length}` }}
        >
          {hours.map((h) => (
            <div key={h} className="border-b border-dashed border-border/60 pr-1.5 pt-0.5" style={{ height: HOUR_PX }}>
              {fmtHourGutter(h)}
            </div>
          ))}
        </div>

        {days.map((d) => {
          const cur = isToday(d);
          const dayEvents = timedEvents.filter((e) => isSameDay(e.start, d));
          return (
            <div
              key={`col-${d.toISOString()}`}
              className={cn('relative border-r border-border last:border-r-0', cur && 'bg-stevens-indigo/[0.03]')}
              style={{ gridRow: `span ${hours.length}`, height: hours.length * HOUR_PX }}
            >
              {hours.map((_, i) => (
                <div
                  key={i}
                  className="absolute left-0 right-0 border-b border-dashed border-border/60"
                  style={{ top: i * HOUR_PX, height: HOUR_PX }}
                />
              ))}
              {dayEvents.map((e) => {
                const startMin = e.start.getHours() * 60 + e.start.getMinutes();
                const endMin = e.end.getHours() * 60 + e.end.getMinutes();
                const startBase = START_HOUR * 60;
                const top = ((Math.max(startMin, startBase) - startBase) / 60) * HOUR_PX;
                const heightPx = Math.max(((Math.min(endMin, END_HOUR * 60 + 60) - Math.max(startMin, startBase)) / 60) * HOUR_PX, 24);
                const tokens = CATEGORY_TOKENS[e.category];
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => onEventClick?.(e)}
                    className={cn(
                      'absolute left-1 right-1 rounded px-2 py-1 text-left overflow-hidden border text-label font-medium leading-tight',
                      tokens.weekBg,
                      e.source === 'stevens-suggestion' && 'border-dashed',
                      isCancelledEvent(e) && 'line-through opacity-60',
                    )}
                    style={{ top, height: heightPx }}
                    title={eventTitle(e, t)}
                  >
                    <div className="font-mono text-meta opacity-75 not-italic">{fmtTimeShort(e.start)}</div>
                    <div className="truncate">{eventTitle(e, t)}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Month view (grid of cells) ────────────────────────────────────────────

interface MonthViewProps {
  events: UnifiedEvent[];
  monthAround: Date;
  onSelectDay?: (day: Date) => void;
  onEventClick?: (event: UnifiedEvent) => void;
}

export function MonthView({ events, monthAround, onSelectDay, onEventClick }: MonthViewProps) {
  const { t, i18n } = useTranslation(['calendar', 'common']);
  const isPhone = useMediaQuery(PHONE_QUERY);
  const weekStartsOn = weekStartFor(i18n.language);
  const monthStart = startOfMonth(monthAround);
  const monthEnd = endOfMonth(monthAround);
  const gridStart = startOfWeek(monthStart, { weekStartsOn });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn });
  const cells = eachDayOfInterval({ start: gridStart, end: gridEnd });
  // The header is the grid's own first week, named by Intl — so it starts on
  // the same day the grid does, in the reader's language.
  const weekdayHeads = cells.slice(0, 7);

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-7">
        {weekdayHeads.map((d) => (
          <MonoLabel
            key={d.toISOString()}
 className="bg-muted border-b border-border px-2.5 py-2"
          >
            {formatWeekday(d)}
          </MonoLabel>
        ))}
        {cells.map((d, i) => {
          const cur = isToday(d);
          const muted = !isSameMonth(d, monthAround);
          const dayEvents = events.filter((e) => isSameDay(e.start, d)).sort(compareEvents);
          const visible = dayEvents.slice(0, 3);
          const more = dayEvents.length - visible.length;
          const isLastCol = (i + 1) % 7 === 0;
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => onSelectDay?.(d)}
              className={cn(
                'relative flex flex-col gap-1 text-left border-b border-border transition-colors hover:bg-muted/40',
                isPhone ? 'px-1.5 py-1.5 min-h-[56px]' : 'px-2.5 py-2 min-h-[88px]',
                !isLastCol && 'border-r',
                muted && 'bg-muted/30',
                cur && 'bg-stevens-indigo/[0.05]',
              )}
            >
              <span
                className={cn(
                  'font-mono text-caption font-medium',
                  muted ? 'text-stevens-ink-3' : 'text-foreground/80',
                  cur && 'text-stevens-indigo font-semibold',
                )}
              >
                {dayNumber(d)}
              </span>
              {isPhone ? (
                // A phone cell (#2419): one category dot per event and "+n".
                // The titles stay in the cell's accessible name; tapping the
                // cell opens that day's agenda, where they read in full.
                <div className="flex flex-wrap items-center gap-1">
                  {visible.map((e) => (
                    <span
                      key={e.id}
                      data-testid="month-dot"
                      aria-hidden
                      className={cn(
                        'w-[7px] h-[7px] shrink-0 rounded-full',
                        CATEGORY_TOKENS[e.category].dot,
                        isCancelledEvent(e) && 'opacity-60',
                      )}
                    />
                  ))}
                  {more > 0 && (
                    <span aria-hidden className="font-mono text-meta text-stevens-ink-3">+{more}</span>
                  )}
                  {visible.map((e) => (
                    // A space between names: the spans sit inline in the accessible name.
                    <span key={`sr-${e.id}`} className="sr-only">{' '}{eventTitle(e, t)}</span>
                  ))}
                  {more > 0 && <span className="sr-only">{' '}{t('views.more', { count: more })}</span>}
                </div>
              ) : (
              <div className="flex flex-col gap-0.5">
                {visible.map((e) => {
                  const tokens = CATEGORY_TOKENS[e.category];
                  return (
                    <span
                      key={e.id}
                      onClick={(ev) => { ev.stopPropagation(); onEventClick?.(e); }}
                      className={cn(
                        'truncate rounded px-1.5 py-px text-label font-medium leading-tight',
                        tokens.monthChip,
                        isCancelledEvent(e) && 'line-through opacity-60',
                      )}
                      title={e.allDay ? eventTitle(e, t) : `${fmtTimeShort(e.start)} ${eventTitle(e, t)}`}
                    >
                      {/* #2410: an all-day chip carries no clock. */}
                      {!e.allDay && (
                        <><span className="font-mono text-meta opacity-70 not-italic">{fmtTimeShort(e.start)}</span>{' '}</>
                      )}
                      {eventTitle(e, t)}
                    </span>
                  );
                })}
                {more > 0 && (
                  <span className="font-mono text-meta text-stevens-ink-3 px-1.5">
                    {t('views.more', { count: more })}
                  </span>
                )}
              </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Category filter panel (the toolbar popover's toggles) ─────────────────

interface CalendarFilterPanelProps {
  hidden: Set<EventCategory>;
  onToggle: (cat: EventCategory) => void;
}

const LEGEND_ORDER: EventCategory[] = ['medical', 'task', 'medication', 'stevens'];

/**
 * The old `CategoryLegend` — four toggle chips with their colour dots — moved
 * inside the toolbar's filter popover (declutter 2026-09-10, spec §5 / C4). A
 * row of chips above a four-entry grid was chrome that outweighed its
 * content; in the popover the dots still explain the colours without sitting
 * on the page. "Show" is a field label, not a mono eyebrow.
 */
export function CalendarFilterPanel({ hidden, onToggle }: CalendarFilterPanelProps) {
  const { t } = useTranslation(['calendar', 'common']);
  const companionName = useCompanionName();
  return (
    <div className="space-y-2">
      <FieldLabel>{t('filter.show')}</FieldLabel>
      <div className="flex flex-wrap gap-2">
      {LEGEND_ORDER.map((c) => {
        const tokens = CATEGORY_TOKENS[c];
        const dim = hidden.has(c);
        const label = categoryLabel(c, companionName, t);
        return (
          <button
            key={c}
            type="button"
            aria-pressed={!dim}
            onClick={() => onToggle(c)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-caption font-medium text-foreground transition-colors hover:bg-muted',
              dim && 'opacity-50 line-through',
            )}
          >
            <span className={cn('w-2 h-2 rounded-full', tokens.dot)} />
            {label}
          </button>
        );
      })}
      </div>
    </div>
  );
}
